import React, { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import CropImageField from '@components/common/form/CropImageField';
import DropdownField from '@components/common/form/DropdownField';
import { AppConstants, DATE_DISPLAY_FORMAT, DATE_FORMAT_DISPLAY, DEFAULT_FORMAT, DEFAULT_FORMAT_ZERO_SECOND, TIME_FORMAT_DISPLAY } from '@constants';
import apiConfig from '@constants/apiConfig';
import { commonLanguages, paypalCurrentcyCodes, restaurantServices, RestaurantStatus } from '@constants/masterData';
import useBasicForm from '@hooks/useBasicForm';
import useFetch from '@hooks/useFetch';
import useTranslate from '@hooks/useTranslate';
import { BaseForm, DatePickerField, NumericField, SelectField, TextField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { convertUtcToLocalTime, getDefaultSettings, handleRemoveImage } from '@utils';
import { Card, Col, Form, Row } from 'antd';
import dayjs from 'dayjs';

const message = defineMessages({
    objectName: 'restaurant',
    requiredExtendDate: 'Extend date is required',
    requiredExpiredDate: 'Expiration date is required',
    validateDueDate: 'The end date must be later than the start date.',
});

const RestaurantForm = (props) => {
    const translate = useTranslate();
    const { formId, actions, dataDetail, onSubmit, setIsChangedFormValues, branchs, isEditing, cardProps } = props;
    const { execute: executeUpFile } = useFetch(apiConfig.file.upload);
    const [imageUrl, setImageUrl] = useState(null);
    const [bannerUrl, setBannerUrl] = useState(null);
    const statusValues = translate.formatKeys(RestaurantStatus, ['label']);
    const [expiredDateDefault, setExpiredDateDefault] = useState();

    const { form, mixinFuncs, onValuesChange } = useBasicForm({
        onSubmit,
        setIsChangedFormValues,
    });

    const uploadFile = (file) => {
        executeUpFile({
            data: {
                type: 'LOGO',
                file: file,
            },
            onCompleted: (response) => {
                if (response.result === true) {
                    setImageUrl(response.data.filePath);
                    setIsChangedFormValues(true);
                }
            },
            onError: (error) => { },
        });
    };
    const uploadBanner = (file) => {
        executeUpFile({
            data: {
                type: 'LOGO',
                file: file,
            },
            onCompleted: (response) => {
                if (response.result === true) {
                    setBannerUrl(response.data.filePath);
                    setIsChangedFormValues(true);
                }
            },
            onError: (error) => { },
        });
    };

    const handleSubmit = (values) => {
        const formattedValues = {
            ...values,
            expiredDate: values.expiredDate ? dayjs(values.expiredDate).format(DEFAULT_FORMAT) : null,
            extendDate: values.extendDate ? dayjs(values.extendDate).format(DEFAULT_FORMAT) : null,
            logoPath: imageUrl,
            bannerPath: bannerUrl,
            settings: dataDetail.settings || getDefaultSettings(),
            accountId: dataDetail.accountId,
            services: Array.isArray(values.services) ? values.services.join(';') : values.services,
        };

        return mixinFuncs.handleSubmit(formattedValues);
    };

    useEffect(() => {
        const toDayjs = (value) => {
            if (!value) return undefined;
            if (dayjs.isDayjs(value)) return value;
            return dayjs(convertUtcToLocalTime(value, DEFAULT_FORMAT, DEFAULT_FORMAT), DEFAULT_FORMAT_ZERO_SECOND);
        };
        form.setFieldsValue({
            ...dataDetail,
            expiredDate: toDayjs(dataDetail.expiredDate),
            extendDate: toDayjs(dataDetail.extendDate),
            latitude: dataDetail.latitude,
            longitude: dataDetail.longitude,
            services: dataDetail.services
                ?.split(';')
                .filter((item, index, self) => item && self.indexOf(item) === index),
        });
        setBannerUrl(dataDetail.bannerPath);
        setImageUrl(dataDetail.logoPath);
    }, [dataDetail]);

    const validateDueDate = (_, value) => {
        const { expiredDate } = form.getFieldValue();
        if (expiredDate && value && value.isBefore(expiredDate)) {
            return Promise.reject(translate.formatMessage(message.validateDueDate));
        }
        return Promise.resolve();
    };

    return (
        <BaseForm id={formId} onFinish={handleSubmit} form={form} onValuesChange={onValuesChange} size='100%'>
            <Card className="card-form" bordered={false} {...cardProps}>
                <Row gutter={16}>
                    {isEditing && <Col span={24} style={{ fontWeight: 600, marginBottom: 12 }}>
                        <span >Tenant ID:</span> {' '}
                        {dataDetail?.tenantId}
                    </Col>}
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.restaurantName)}
                            name="restaurantName"
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.restaurantPath)}
                            name="restaurantPath"
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <CropImageField
                            label={translate.formatMessage(commonMessage.logoPath)}
                            name="logoPath"
                            imageUrl={imageUrl && `${AppConstants.contentRootUrl}${imageUrl}`}
                            aspect={1 / 1}
                            uploadFile={uploadFile}
                            handleRemoveImage={() => handleRemoveImage(setImageUrl, form)}
                            required
                        />
                    </Col>
                    <Col span={12}>
                        <CropImageField
                            label={translate.formatMessage(commonMessage.bannerPath)}
                            name="bannerPath"
                            imageUrl={bannerUrl && `${AppConstants.contentRootUrl}${bannerUrl}`}
                            aspect={40 / 9}
                            style={{ width: 170 }}
                            uploadFile={uploadBanner}
                            handleRemoveImage={() => handleRemoveImage(setBannerUrl, form)}
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <TextField
                            label={translate.formatMessage(commonMessage.hotline)}
                            name="hotline"
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.zipCode)}
                            name="zipCode"
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.city)}
                            name="city"
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.address)}
                            name="address"
                        />
                    </Col>
                    <Col span={12}>
                        <NumericField
                            formatter={(v) => v}
                            parser={(v) => v}
                            label={translate.formatMessage(commonMessage.latitude)}
                            name="latitude"
                        />
                    </Col>
                    <Col span={12}>
                        <NumericField
                            formatter={(v) => v}
                            parser={(v) => v}
                            label={translate.formatMessage(commonMessage.longitude)}
                            name="longitude"
                        />
                    </Col>
                    <Col span={12}>
                        <DatePickerField
                            label={<FormattedMessage defaultMessage="Expiration date" />}
                            name="expiredDate"
                            format={DATE_FORMAT_DISPLAY}
                            onChange={(value) => {
                                setExpiredDateDefault(value);
                            }}
                            disabledDate={(current) => current && current.isBefore(dayjs().add(1, 'day'), 'day')}
                            rules={[
                                {
                                    required: true,
                                    message: translate.formatMessage(message.requiredExpiredDate),
                                },
                                // {
                                //     validator: validateStartDate,
                                // },
                            ]}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col span={12}>
                        <DatePickerField
                            label={<FormattedMessage defaultMessage="Extend Date" />}
                            name="extendDate"
                            format={DATE_FORMAT_DISPLAY}
                            disabledDate={(current) => current && current.isBefore(dayjs().add(1, 'day'), 'day')}
                            // disabledDate={}
                            rules={[
                                {
                                    required: true,
                                    message: translate.formatMessage(message.requiredExtendDate),
                                },
                                {
                                    validator: validateDueDate,
                                },
                            ]}
                            style={{ width: '100%' }}
                        />
                    </Col>

                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <SelectField
                            name="services"
                            label={translate.formatMessage(commonMessage.services)}
                            options={restaurantServices}
                            required
                            mode="multiple"
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            name="paypalClientId"
                            label={translate.formatMessage(commonMessage.paypalClientId)}
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <TextField
                            name="paypalClientSecrect"
                            label={translate.formatMessage(commonMessage.paypalClientSecrect)}
                        />
                    </Col>
                    <Col span={12}>
                        <SelectField
                            name="paypalCurrentcyCode"
                            label={translate.formatMessage(commonMessage.paypalCurrentcyCode)}
                            options={paypalCurrentcyCodes}
                        />
                    </Col>
                    <Col span={12}>
                        <SelectField
                            required
                            name="lang"
                            label={translate.formatMessage(commonMessage.language)}
                            options={commonLanguages}
                        />
                    </Col>
                    <Col span={12}>
                        <SelectField
                            required
                            label={<FormattedMessage defaultMessage="Status" />}
                            name="status"
                            options={statusValues}
                        />
                    </Col>
                </Row>
                <div className="footer-card-form">{actions}</div>
            </Card>
        </BaseForm>
    );
};

export default RestaurantForm;
